import { updateOffersBatch, determineInsertOrUpdate } from './realtimeOfferService.js';
import { usePersistedDataStore } from '../../../store.js';
import { applyOfferRowPatch, buildDerivedOfferFieldsForRow } from './offerGridUpdateService.js';
import { confirmIfDeepReduction } from '../components/DeepReductionConfirmDialog.js';
import logger from '../../../../utils/logger';

/**
 * Simple wrapper that checks for insert actions and shows dialog if needed
 * Improved on an old services based approach, but still somewhat convoluted given this then renders a Seperate dialog and is ugly as hell to invoke.
 */
export const updateOfferWithDialog = (parcelId, offerData, context, options = {}, onSuccess = null, onError = null, onShowDialog = null, oldValues = null, apiRef = null, currentRowData = null) => {
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const row = currentRowData || getNegotiationObj?.cases?.find((c) => c.parcel_id === parcelId);
  const offerValue = offerData.OfferValue || offerData.TownOffer || offerData.PortfolioOffer;

  const revertOffer = () => {
    if (!oldValues) return;
    const field = offerData.OfferValue !== undefined ? 'OfferValue'
      : offerData.TownOffer !== undefined ? 'TownOffer'
      : offerData.PortfolioOffer !== undefined ? 'PortfolioOffer' : null;
    if (!field) return;
    const originalValue = oldValues[field];
    const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
    if (setNegotiationObj && getNegotiationObj) {
      const updatedNegotiationObj = { ...getNegotiationObj };
      const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === parcelId);
      if (caseIndex !== -1) {
        updatedNegotiationObj.cases[caseIndex][field] = originalValue;
        setNegotiationObj(updatedNegotiationObj);
      }
    }
    if (apiRef?.current && currentRowData) {
      if (field === 'OfferValue' || field === 'TownOffer') {
        applyOfferRowPatch(
          apiRef,
          parcelId,
          buildDerivedOfferFieldsForRow(currentRowData, { [field]: originalValue }),
        );
      } else {
        apiRef.current.setEditCellValue({ id: parcelId, field, value: originalValue });
        try { apiRef.current.stopCellEditMode({ id: parcelId, field }); } catch (e) { /* not in edit mode */ }
      }
    }
  };

  const persist = async (offer, opts = options) => {
    if (!(await confirmIfDeepReduction(offer.offerValue ?? offerValue, row))) {
      revertOffer();
      return;
    }
    updateOffersBatch([offer], context, opts).then(onSuccess).catch(onError);
  };

  // If no dialog callback provided, just proceed normally
  if (!onShowDialog) {
    persist({
      parcelId,
      offerValue,
      offeredBy: offerData.OfferValue !== undefined ? 'Aventine' :
                 offerData.TownOffer !== undefined ? 'Town' :
                 offerData.PortfolioOffer !== undefined ? 'Aventine' : 'Aventine',
      offerDate: new Date().toISOString(),
      comments: offerData.Comments,
      ...offerData
    });
    return;
  }

  try {
    const offeredBy = offerData.OfferValue !== undefined ? 'Aventine' :
                     offerData.TownOffer !== undefined ? 'Town' :
                     offerData.PortfolioOffer !== undefined ? 'Aventine' : null;

    if (offeredBy) {
      const offer = { parcelId, offerValue, offeredBy };

      let dataForDetermination;
      if (currentRowData) {
        dataForDetermination = {
          ...getNegotiationObj,
          cases: [{
            parcel_id: parcelId,
            OfferValue: currentRowData.OfferValue,
            TownOffer: currentRowData.TownOffer,
            PortfolioOffer: currentRowData.PortfolioOffer,
            Negotiations: currentRowData.Negotiations || []
          }]
        };
      } else {
        dataForDetermination = getNegotiationObj;
      }

      const insertOrUpdate = determineInsertOrUpdate(dataForDetermination, offer, options.round);
      const shouldShowDialog = insertOrUpdate.action === 'insert' && insertOrUpdate.showDialog;

      if (shouldShowDialog) {
        const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
        if (setNegotiationObj) {
          const updatedNegotiationObj = { ...getNegotiationObj };
          const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === parcelId);

          if (caseIndex !== -1) {
            if (offerData.OfferValue !== undefined) {
              updatedNegotiationObj.cases[caseIndex].OfferValue = offerData.OfferValue;
            } else if (offerData.TownOffer !== undefined) {
              updatedNegotiationObj.cases[caseIndex].TownOffer = offerData.TownOffer;
            } else if (offerData.PortfolioOffer !== undefined) {
              updatedNegotiationObj.cases[caseIndex].PortfolioOffer = offerData.PortfolioOffer;
            }
            setNegotiationObj(updatedNegotiationObj);
          }
        }

        const caseData = dataForDetermination.cases.find(c => c.parcel_id === parcelId);
        const negotiationRounds = caseData?.Negotiations || [];

        onShowDialog({
          parcelId,
          newRoundNumber: insertOrUpdate.round || null,
          negotiationRounds,
          offeredBy,
          newOfferValue: offerValue,
          onConfirm: () => {
            persist({
              parcelId,
              offerValue,
              offeredBy,
              offerDate: new Date().toISOString(),
              comments: offerData.Comments,
              preDeterminedAction: insertOrUpdate,
              ...offerData
            }, { ...options, round: insertOrUpdate.round });
          },
          onEditPriorRound: (roundNumber) => {
            persist({
              parcelId,
              offerValue,
              offeredBy,
              offerDate: new Date().toISOString(),
              comments: offerData.Comments,
              preDeterminedAction: { ...insertOrUpdate, round: roundNumber },
              ...offerData
            }, { ...options, round: roundNumber });
          },
          onCancel: revertOffer,
        });
        return;
      } else if (insertOrUpdate.action === 'insert') {
        persist({
          parcelId,
          offerValue,
          offeredBy,
          offerDate: new Date().toISOString(),
          comments: offerData.Comments,
          preDeterminedAction: insertOrUpdate,
          ...offerData
        });
        return;
      }
    }
  } catch (error) {
    logger.error('Error checking insert action:', error);
  }

  persist({
    parcelId,
    offerValue,
    offeredBy: offerData.OfferValue !== undefined ? 'Aventine' :
               offerData.TownOffer !== undefined ? 'Town' :
               offerData.PortfolioOffer !== undefined ? 'Aventine' : 'Aventine',
    offerDate: new Date().toISOString(),
    comments: offerData.Comments,
    ...offerData
  });
};
